import React, { ReactNode } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container,
  Menu,
  MenuItem,
  IconButton,
  Chip
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  Dashboard,
  Business,
  People,
  History,
  Groups,
  Tune,
  SmartToy,
  Storefront,
  Handshake,
  LocalOffer
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { employeePortalUrl } from '../utils/employeePortal';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [portalsAnchor, setPortalsAnchor] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const navigationItems = [
    { path: '/', label: t('navigation.dashboard'), icon: <Dashboard /> },
    { path: '/customers', label: t('navigation.customers'), icon: <Groups /> },
    { path: '/tenants', label: t('navigation.tenants'), icon: <Business /> },
    { path: '/client-accounts', label: t('navigation.clientAccounts', 'Cuentas de cliente'), icon: <SmartToy /> },
    // Partners REALES (resellers CSP con portal propio) — no confundir con
    // /users, que son los usuarios administradores del marketplace.
    { path: '/partners', label: t('navigation.partners', 'Partners'), icon: <Handshake /> },
    // Catálogo de PAQUETES DE CRÉDITOS del producto «empleados IA» (precios y
    // el «para qué vale» de cada paquete). No confundir con la facturación
    // Paddle de tenants, que vive en la tabla `plans`.
    { path: '/credit-packages', label: t('navigation.creditPackages', 'Paquetes de créditos'), icon: <LocalOffer /> },
    { path: '/deployments', label: t('navigation.deployments'), icon: <History /> },
    ...(user?.role === 'super_admin' ? [
      { path: '/users', label: t('navigation.adminUsers', 'Usuarios admin'), icon: <People /> },
      { path: '/sizes', label: t('navigation.sizes'), icon: <Tune /> }
    ] : [])
  ];

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {t('common.appTitle')}
          </Typography>

          {/* Portales: accesos a las otras caras del marketplace. Los que viven
              en ESTA SPA van por origin (valen en cualquier entorno); el catálogo
              de empleados es otra SPA → env con fallback al dominio productivo. */}
          <Button
            color="inherit"
            startIcon={<Storefront />}
            onClick={(e) => setPortalsAnchor(e.currentTarget)}
            sx={{ mr: 1, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
          >
            {t('navigation.portals', 'Portales')}
          </Button>
          <Menu
            anchorEl={portalsAnchor}
            open={Boolean(portalsAnchor)}
            onClose={() => setPortalsAnchor(null)}
          >
            {[
              { label: t('navigation.portalEmployees', 'Catálogo de empleados'),
                href: employeePortalUrl() },
              { label: t('navigation.portalCustomer', 'Portal de clientes'),
                href: `${window.location.origin}/customer/login` },
              { label: t('navigation.portalPartner', 'Portal de partners'),
                href: `${window.location.origin}/partner/login` },
            ].map((p) => (
              <MenuItem key={p.href} onClick={() => { window.open(p.href, '_blank', 'noopener'); setPortalsAnchor(null); }}>
                {p.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 3 }}>
            {navigationItems.map((item) => (
              <Button
                key={item.path}
                color="inherit"
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                sx={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  bgcolor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Language Selector */}
          <LanguageSelector />

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={user.role === 'super_admin' ? t('resellers.isSuperAdmin') : t('resellers.role')}
                size="small"
                variant="outlined"
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  fontSize: '0.75rem'
                }}
              />
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleProfile}>
                  <AccountCircle sx={{ mr: 1 }} />
                  {t('navigation.profile')}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ExitToApp sx={{ mr: 1 }} />
                  {t('auth.logout')}
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
        <Container maxWidth="xl">
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;